/**
 * Lightweight fiducial marker detector — pure JS, no OpenCV.
 *
 * Detects 4 markers at expected positions (near calibrated paper corners).
 * Uses ROI-based scanning: only searches a small region around each expected
 * corner, keeping CPU cost minimal (~2ms total for 4 markers).
 *
 * Detection pipeline per ROI:
 * 1. Extract ROI from video frame (grayscale)
 * 2. Adaptive threshold (local mean)
 * 3. Find the largest dark square-like contour
 * 4. Sample 5×5 inner grid to read marker ID
 * 5. Return refined corner position if ID matches expected
 */

import { MARKER_PATTERNS } from './MarkerPatterns';
import type { Point2D } from './Homography';

/** Threshold for how many cells must match to accept a marker (out of 25). */
const MIN_MATCH_CELLS = 20;

/**
 * Detect all 4 paper corner markers in the given video frame.
 *
 * @param video The video element to read from
 * @param expectedCorners Approximate corner positions (normalized 0-1), from
 *        previous frame or calibration. Order: TL, TR, BR, BL.
 * @param roiSize ROI half-size in pixels around each expected corner
 * @param offCanvas Reusable offscreen canvas (avoids allocation per frame)
 * @returns 4 refined corner positions (normalized 0-1) if all found, null otherwise
 */
export function detectMarkers(
  video: HTMLVideoElement,
  expectedCorners: Point2D[],
  roiSize: number,
  offCanvas: HTMLCanvasElement,
): Point2D[] | null {
  if (expectedCorners.length < 4) return null;

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;

  // Ensure offscreen canvas matches video dimensions
  if (offCanvas.width !== vw || offCanvas.height !== vh) {
    offCanvas.width = vw;
    offCanvas.height = vh;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  // Draw current video frame (only once for all ROIs)
  ctx.drawImage(video, 0, 0, vw, vh);

  const results: Point2D[] = [];

  for (let id = 0; id < 4; id++) {
    const ex = expectedCorners[id].x * vw;
    const ey = expectedCorners[id].y * vh;

    // ROI bounds (clamped to frame)
    const rx = Math.max(0, Math.round(ex - roiSize));
    const ry = Math.max(0, Math.round(ey - roiSize));
    const rw = Math.min(vw - rx, roiSize * 2);
    const rh = Math.min(vh - ry, roiSize * 2);
    if (rw < 20 || rh < 20) return null;

    const imageData = ctx.getImageData(rx, ry, rw, rh);
    const gray = toGrayscale(imageData);
    const binary = adaptiveThreshold(gray, rw, rh);
    const quad = findDarkQuad(binary, rw, rh);
    if (!quad) return null;

    // Read 5×5 inner grid from the quad
    const readResult = readMarkerGrid(gray, rw, quad);
    if (!readResult) return null;

    // Match against expected pattern
    const pattern = MARKER_PATTERNS[id];
    let matchCount = 0;
    for (let i = 0; i < 25; i++) {
      if (readResult[i] === pattern[i]) matchCount++;
    }
    if (matchCount < MIN_MATCH_CELLS) return null;

    // Refined center = center of detected quad, converted back to normalized coords
    const cx = (quad.cx + rx) / vw;
    const cy = (quad.cy + ry) / vh;
    results.push({ x: cx, y: cy });
  }

  return results.length === 4 ? results : null;
}

/**
 * Detect markers at expected corner positions, returning per-corner results.
 * Unlike {@link detectMarkers} this does NOT bail out when some corners fail —
 * each slot is independently a refined Point2D or null. Caller can decide to
 * use 4-point full DLT, 3-point affine fallback, or treat as fully lost.
 */
export function detectMarkersPartial(
  video: HTMLVideoElement,
  expectedCorners: Point2D[],
  roiSize: number,
  offCanvas: HTMLCanvasElement,
): (Point2D | null)[] {
  const empty: (Point2D | null)[] = [null, null, null, null];
  if (expectedCorners.length < 4) return empty;

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return empty;

  if (offCanvas.width !== vw || offCanvas.height !== vh) {
    offCanvas.width = vw;
    offCanvas.height = vh;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return empty;

  ctx.drawImage(video, 0, 0, vw, vh);
  const results: (Point2D | null)[] = [null, null, null, null];

  for (let id = 0; id < 4; id++) {
    const ex = expectedCorners[id].x * vw;
    const ey = expectedCorners[id].y * vh;
    const rx = Math.max(0, Math.round(ex - roiSize));
    const ry = Math.max(0, Math.round(ey - roiSize));
    const rw = Math.min(vw - rx, roiSize * 2);
    const rh = Math.min(vh - ry, roiSize * 2);
    if (rw < 20 || rh < 20) continue;

    const imageData = ctx.getImageData(rx, ry, rw, rh);
    const gray = toGrayscale(imageData);
    const binary = adaptiveThreshold(gray, rw, rh);
    const quad = findDarkQuad(binary, rw, rh);
    if (!quad) continue;

    const readResult = readMarkerGrid(gray, rw, quad);
    if (!readResult) continue;

    const pattern = MARKER_PATTERNS[id];
    let matchCount = 0;
    for (let i = 0; i < 25; i++) {
      if (readResult[i] === pattern[i]) matchCount++;
    }
    if (matchCount < MIN_MATCH_CELLS) continue;

    results[id] = { x: (quad.cx + rx) / vw, y: (quad.cy + ry) / vh };
  }

  return results;
}


/**
 * Scan a single arbitrary frame position for any matching marker (any of the 4 IDs).
 * The caller must have already drawn the video frame onto {@link ctx} — do NOT call
 * ctx.drawImage inside here. One draw call can be shared across many calls.
 *
 * Used by MultiPaperDetector for full-frame grid scanning.
 *
 * @param ctx  2D context with video already drawn
 * @param vw   Video width in pixels
 * @param vh   Video height in pixels
 * @param nx   Normalized x centre of scan position (0–1)
 * @param ny   Normalized y centre of scan position (0–1)
 * @param roiSize ROI half-size in pixels
 * @returns Match result or null if no marker found
 */
export function detectAnyMarkerAt(
  ctx: CanvasRenderingContext2D,
  vw: number, vh: number,
  nx: number, ny: number,
  roiSize: number,
): { markerId: number; nx: number; ny: number; confidence: number } | null {
  const px = Math.round(nx * vw);
  const py = Math.round(ny * vh);
  const rx = Math.max(0, px - roiSize);
  const ry = Math.max(0, py - roiSize);
  const rw = Math.min(vw - rx, roiSize * 2);
  const rh = Math.min(vh - ry, roiSize * 2);
  if (rw < 20 || rh < 20) return null;

  const imageData = ctx.getImageData(rx, ry, rw, rh);
  const gray = toGrayscale(imageData);
  const binary = adaptiveThreshold(gray, rw, rh);
  const quad = findDarkQuad(binary, rw, rh);
  if (!quad) return null;

  const readResult = readMarkerGrid(gray, rw, quad);
  if (!readResult) return null;

  let bestId = -1;
  let bestMatch = 0;
  for (let id = 0; id < 4; id++) {
    const pattern = MARKER_PATTERNS[id];
    let matchCount = 0;
    for (let i = 0; i < 25; i++) {
      if (readResult[i] === pattern[i]) matchCount++;
    }
    if (matchCount > bestMatch) { bestMatch = matchCount; bestId = id; }
  }

  if (bestId < 0 || bestMatch < MIN_MATCH_CELLS) return null;
  return { markerId: bestId, nx, ny, confidence: bestMatch / 25 };
}
// ── Internal helpers ──────────────────────────────────────

function toGrayscale(imageData: ImageData): Uint8Array {
  const { data, width, height } = imageData;
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const j = i * 4;
    gray[i] = Math.round(data[j] * 0.299 + data[j + 1] * 0.587 + data[j + 2] * 0.114);
  }
  return gray;
}

function adaptiveThreshold(gray: Uint8Array, w: number, h: number, blockSize = 15): Uint8Array {
  const binary = new Uint8Array(w * h);
  const halfBlock = Math.floor(blockSize / 2);

  // Integral image for fast local mean
  const integral = new Float64Array((w + 1) * (h + 1));
  for (let y = 0; y < h; y++) {
    let rowSum = 0;
    for (let x = 0; x < w; x++) {
      rowSum += gray[y * w + x];
      integral[(y + 1) * (w + 1) + (x + 1)] = rowSum + integral[y * (w + 1) + (x + 1)];
    }
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const x1 = Math.max(0, x - halfBlock);
      const y1 = Math.max(0, y - halfBlock);
      const x2 = Math.min(w - 1, x + halfBlock);
      const y2 = Math.min(h - 1, y + halfBlock);
      const area = (x2 - x1 + 1) * (y2 - y1 + 1);
      const sum = integral[(y2 + 1) * (w + 1) + (x2 + 1)]
                - integral[y1 * (w + 1) + (x2 + 1)]
                - integral[(y2 + 1) * (w + 1) + x1]
                + integral[y1 * (w + 1) + x1];
      const mean = sum / area;
      binary[y * w + x] = gray[y * w + x] < mean - 12 ? 1 : 0;
    }
  }
  return binary;
}

/** Find the largest dark rectangular region. Returns bounding box + center. */
function findDarkQuad(
  binary: Uint8Array, w: number, h: number,
): { x: number; y: number; qw: number; qh: number; cx: number; cy: number } | null {
  // Simple approach: find connected dark region via flood-fill from the darkest area
  // For speed, use bounding box of all dark pixels (assumes marker is the dominant dark object in ROI)
  let minX = w, maxX = 0, minY = h, maxY = 0;
  let darkCount = 0;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (binary[y * w + x] === 1) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        darkCount++;
      }
    }
  }

  const qw = maxX - minX;
  const qh = maxY - minY;

  // Sanity checks: quad must be roughly square and not too small/large
  if (qw < 10 || qh < 10) return null;
  const aspect = qw / qh;
  if (aspect < 0.5 || aspect > 2.0) return null;
  // Dark pixels should fill a reasonable portion of the bounding box
  const fillRatio = darkCount / (qw * qh);
  if (fillRatio < 0.3) return null;

  return { x: minX, y: minY, qw, qh, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

/** Sample the 5×5 inner grid of a detected quad. Returns binary array (1=black, 0=white). */
function readMarkerGrid(
  gray: Uint8Array, w: number,
  quad: { x: number; y: number; qw: number; qh: number },
): number[] | null {
  // The marker has 1-cell border → inner area starts at 1/7 and ends at 6/7
  const cellW = quad.qw / 7;
  const cellH = quad.qh / 7;
  if (cellW < 2 || cellH < 2) return null;

  const result: number[] = [];
  // Compute overall mean luminance in the inner area for threshold
  let totalLum = 0;
  let count = 0;
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const sx = Math.round(quad.x + (c + 1.5) * cellW);
      const sy = Math.round(quad.y + (r + 1.5) * cellH);
      if (sx >= 0 && sx < w && sy >= 0) {
        totalLum += gray[sy * w + sx];
        count++;
      }
    }
  }
  if (count === 0) return null;
  const midLum = totalLum / count;

  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const sx = Math.round(quad.x + (c + 1.5) * cellW);
      const sy = Math.round(quad.y + (r + 1.5) * cellH);
      if (sx < 0 || sx >= w || sy < 0) {
        result.push(0);
      } else {
        result.push(gray[sy * w + sx] < midLum ? 1 : 0);
      }
    }
  }
  return result;
}
